import React, { useCallback, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  AlertTriangle,
  Building2,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  Circle,
  ExternalLink,
  FileCheck2,
  Landmark,
  LockKeyhole,
  Plus,
  RefreshCw,
  Search,
  ShieldCheck,
  Trash2,
  Truck,
  UserRound,
  UsersRound,
  X,
} from 'lucide-react';
import { Empty, SkeletonTableRows } from '../ui/kit';
import { useAuth } from '../App';
import { useLocation, useNavigate } from 'react-router-dom';
import { decisionErrorMessage } from '../lib/docReviewLogic.mjs';
import './Partners.css';
import PageHeader from '../components/PageHeader';
import { ReviewDrawerById } from '../components/ReviewDrawer';   // FAHRER-KI-ABLAUFDATEN-BEDIENUNG-001: Prüfbereich bleibt im Reiter
import { datumUndZeit, nurDatum } from '../lib/zeit';

const STATUS = {
  approved: { label: 'Freigegeben', color: '#547860' },
  pending: { label: 'In Prüfung', color: '#7a6680' },
  rejected: { label: 'Abgelehnt', color: '#a15a5a' },
  deleted: { label: 'Gelöscht', color: '#8b9099' },
};

const FILTERS = [
  { value: 'pending', label: 'In Prüfung', hint: 'Offene Compliance-Queue' },
  { value: 'approved', label: 'Freigegeben', hint: 'Aktive Partnerkonten' },
  { value: 'rejected', label: 'Abgelehnt', hint: 'Konten mit Nachbesserung' },
  { value: 'all', label: 'Alle Partner', hint: 'Seitenweise durchsuchen' },
  { value: 'deleted', label: 'Gelöscht', hint: 'Archivierte Partnerkonten' },
];


// PARTNER-KORREKTUR-001: direkte Gutschrift/Belastung auf das PARTNERKONTO (ohne Fahrer-
// und Fahrtbezug) — für Fälle, die an keinem Fahrer hängen (Vertragsstrafe, Kulanz,
// Zuschuss, Abrechnungsklärung). Gleiche Wächter wie die Fahrer-Korrektur: unveränderliche
// Buchungszeile, Pflichtgrund, Idempotenz, Sperre bei nicht abrechenbarem Partner (Server).
// Sichtbar nur mit finance.manage.
const parsePartnerEuroCents = (raw) => {
  const value = String(raw).trim().replace(',', '.');
  if (!/^\d+(\.\d{1,2})?$/.test(value)) return null;
  const cents = Math.round(Number(value) * 100);
  return cents > 0 ? cents : null;
};
const newPartnerCorrectionKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID
  ? crypto.randomUUID()
  : `partner-korrektur-${Date.now()}-${Math.random().toString(16).slice(2)}`);

// Der Scope-Token trennt Admin und Zielkonto deterministisch, ohne ihre IDs lesbar abzulegen.
// Kollidierten zwei Tokens theoretisch, stoppt der serverseitige Request-Fingerprint mit 409.
const opaquePartnerCorrectionScope = (...parts) => {
  const input = parts.map(part => `${String(part).length}:${String(part)}`).join('|');
  let left = 0x811c9dc5;
  let right = 0x9e3779b9;
  for (let index = 0; index < input.length; index += 1) {
    const code = input.charCodeAt(index);
    left = Math.imul(left ^ code, 0x01000193);
    right = Math.imul(right ^ code, 0x85ebca6b);
    right ^= right >>> 13;
  }
  return `${(left >>> 0).toString(36)}-${(right >>> 0).toString(36)}`;
};

// SessionStorage hält ausschließlich den Vorgangsschlüssel; Finanzdaten, Grund,
// Partner-Stammdaten und technische IDs werden nie abgelegt.
const partnerCorrectionStorageName = (adminId, partnerId) => (
  adminId && partnerId
    ? `voogo.admin.finance-correction.v1:partner:${opaquePartnerCorrectionScope(adminId, partnerId)}`
    : null
);

const storedPartnerCorrectionKey = (storageName) => {
  if (storageName) {
    try {
      const stored = sessionStorage.getItem(storageName);
      if (stored) return stored;
    } catch {}
  }
  const created = newPartnerCorrectionKey();
  if (storageName) {
    try { sessionStorage.setItem(storageName, created); } catch {}
  }
  return created;
};

const clearStoredPartnerCorrectionKey = (storageName, key) => {
  if (!storageName) return;
  try {
    if (sessionStorage.getItem(storageName) === key) sessionStorage.removeItem(storageName);
  } catch {}
};

const partnerCorrectionResultIsUnclear = (requestError) => {
  const status = requestError?.response?.status;
  return !requestError?.response || !Number.isFinite(status) || status === 408 || status >= 500;
};

function PartnerCorrectionCard({ partnerId, approvalStatus }) {
  const { can, user } = useAuth();
  const allowed = Boolean(can && can('finance.manage'));
  const [direction, setDirection] = useState('belastung');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const storageNameRef = useRef(null);
  const idempotencyRef = useRef(null);
  const inFlightRef = useRef(false);
  if (!allowed) return null;

  if (approvalStatus !== 'approved') {
    return (
      <section className="partners-detail-section partners-korrektur-card">
        <header><div><span className="partners-overline">Partnerkonto</span><h3>Korrekturbuchung</h3></div></header>
        <p>Dieser Partner ist nicht regulär abrechenbar (Status: {approvalStatus || 'unbekannt'}) — Korrekturen sind gesperrt, bis über das Konto entschieden ist.</p>
      </section>
    );
  }

  const submit = async (event) => {
    event.preventDefault();
    if (inFlightRef.current) return;
    const parsed = parsePartnerEuroCents(amount);
    if (parsed === null) { toast.error('Betrag in Euro mit höchstens zwei Nachkommastellen angeben, z. B. 25,00'); return; }
    if (!reason.trim()) { toast.error('Grund ist Pflicht'); return; }

    const storageName = partnerCorrectionStorageName(user?.id, partnerId);
    if (storageNameRef.current !== storageName) {
      storageNameRef.current = storageName;
      idempotencyRef.current = null;
    }
    const requestKey = idempotencyRef.current || storedPartnerCorrectionKey(storageName);
    idempotencyRef.current = requestKey;
    inFlightRef.current = true;
    setSaving(true);
    try {
      await api.post('/admin/finance/corrections', {
        partner_id: partnerId,
        amount_cents: direction === 'belastung' ? -parsed : parsed,
        reason: reason.trim(),
      }, { headers: { 'Idempotency-Key': requestKey } });
      toast.success(direction === 'belastung' ? 'Belastung gebucht — wirkt auf die nächste Auszahlung' : 'Gutschrift gebucht — wirkt auf die nächste Auszahlung');
      clearStoredPartnerCorrectionKey(storageName, requestKey);
      if (idempotencyRef.current === requestKey) idempotencyRef.current = null;
      setAmount('');
      setReason('');
    } catch (requestError) {
      if (partnerCorrectionResultIsUnclear(requestError)) {
        toast.error('Ergebnis unklar – die Buchung wurde nicht sicher bestätigt. Bitte erneut senden; derselbe Vorgangsschlüssel wird wiederverwendet.');
      } else {
        toast.error(requestError.response?.data?.message || 'Korrektur abgelehnt');
      }
    } finally {
      inFlightRef.current = false;
      setSaving(false);
    }
  };

  return (
    <section className="partners-detail-section partners-korrektur-card" aria-label="Korrekturbuchung Partnerkonto">
      <header>
        <div><span className="partners-overline">Partnerkonto</span><h3>Korrekturbuchung</h3></div>
        <span>ohne Fahrer-/Fahrtbezug · unveränderliche Buchungszeile</span>
      </header>
      <p>Belastet oder vergütet das Partnerkonto direkt — z. B. Vertragsstrafe oder Kulanz. Der Partner sieht die Korrektursumme in Portal und Abrechnung; der Grund bleibt im Finanz-Audit. Fahrerbezogene Fälle (Wartezeit, reklamierte Fahrt) bitte weiter über Fahrer-Akte oder Fahrt-Detail buchen.</p>
      <form onSubmit={submit}>
        <select value={direction} onChange={(event) => setDirection(event.target.value)} aria-label="Richtung">
          <option value="belastung">Belasten (−)</option>
          <option value="gutschrift">Gutschreiben (+)</option>
        </select>
        <input
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
          placeholder="Betrag in €, z. B. 25,00"
          inputMode="decimal"
          aria-label="Betrag in Euro"
        />
        <input
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Grund (Pflicht), z. B. Vertragsstrafe laut Vereinbarung"
          aria-label="Grund"
        />
        <button type="submit" disabled={saving}>{saving ? 'Bucht…' : 'Buchen'}</button>
      </form>
    </section>
  );
}

// ADMIN-UX-HEUTE-AKTEN-EINHEIT-001: Klartext für die Akten-Anhänge (Abrechnung + Support).
const PAYOUT_STATE_LABELS = { calculated: 'Berechnet', ready: 'Bereit', exported: 'Exportiert', paid: 'Bezahlt' };
const SUPPORT_STATUS_LABELS = {
  neu: 'Neu', in_bearbeitung: 'In Bearbeitung', geloest: 'Gelöst', nicht_geloest: 'Nicht gelöst',
  open: 'Offen', closed: 'Geschlossen',
};
const euro = (cents) => (Number(cents || 0) / 100).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const PAGE_SIZE = 25;

const DOC_LABELS = {
  gewerbeanmeldung: 'Gewerbeanmeldung',
  handelsregister: 'Handelsregister',
  personalausweis: 'Personalausweis / Aufenthaltstitel',
  konzession: 'Konzession',
  versicherung: 'Versicherung',
  bank_nachweis: 'Bank-/Inhabernachweis',
  steuernachweis: 'Nachweis Steuernummer / USt-IdNr.',   // ADMIN-FAHRZEUG-PARTNER-SPERRE-001
  sonstiges: 'Sonstiges',
};

// ADMIN-FAHRZEUG-PARTNER-SPERRE-001 (Owner 06.10.2026): Betriebssperre mit Grund + Stammdaten-Nachforderung
// in der Partnerakte — gleiche Bedienung wie die Fahrer-Kontosperre (Drivers.js, FAHRER-KI-BACKEND-001).
const SUSPENSION_TONE = { admin: 'is-admin', dokument: 'is-dokument', compliance: 'is-dokument', sonstige: 'is-dokument' };
function PartnerLockSection({ detail, canManage, onLock, onUnlock, onRequestCorrection, onClearCorrection }) {
  const [reasonCode, setReasonCode] = useState('unterlagen');
  const [reason, setReason] = useState('');
  const [unlockReason, setUnlockReason] = useState('');
  const [correction, setCorrection] = useState('');
  const [busy, setBusy] = useState(false);
  const locks = detail.account_locks || {};
  const active = locks.active || null;
  const codes = locks.reason_codes || [];
  const submit = async (fn) => { setBusy(true); try { await fn(); } finally { setBusy(false); } };
  return (
    <section className="partners-detail-section partners-account-lock" data-testid="partner-lock-section">
      <header>
        <div><span className="partners-overline">Betriebssperre · Admin</span><h3>Sperre und Nachforderung</h3></div>
        <span>Fahrten stoppen, Unterlagen oder Angaben nachfordern</span>
      </header>
      {locks.suspension ? (
        <div className={`partners-lock-state ${SUSPENSION_TONE[locks.suspension.kind] || ''}`}>
          <LockKeyhole size={13} />
          <strong>{locks.suspension.label}</strong>
          {active && <span>Grund: {active.reason || '–'} · {active.actor_name || 'Admin'} · {datumUndZeit(active.created_at, { jahr: 'voll', leer: '–' })}</span>}
          {!active && <span>Automatische Sperre des Ablauf-Wächters (Konzession) — löst sich nach gültiger Freigabe.</span>}
        </div>
      ) : (
        <div className="partners-lock-state is-free"><ShieldCheck size={13} /><strong>Nicht gesperrt</strong></div>
      )}
      {canManage && (active ? (
        <div className="partners-lock-form">
          <input value={unlockReason} onChange={(event) => setUnlockReason(event.target.value)} placeholder="Grund für das Entsperren (wird protokolliert)" aria-label="Grund für das Entsperren" />
          <button className="is-unlock" disabled={busy} onClick={() => submit(() => onUnlock(detail.id, unlockReason))}>Betrieb entsperren</button>
        </div>
      ) : (
        <div className="partners-lock-form">
          <select value={reasonCode} onChange={(event) => setReasonCode(event.target.value)} aria-label="Art des Sperrgrundes">
            {codes.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
            {codes.length === 0 && <option value="sonstiges">Sonstiges</option>}
          </select>
          <input value={reason} onChange={(event) => setReason(event.target.value)} placeholder="Sperrgrund (Pflicht) — der Partner sieht ihn" aria-label="Sperrgrund" />
          <button className="is-lock" disabled={busy || !reason.trim()} onClick={() => submit(() => onLock(detail.id, { reason: reason.trim(), reason_code: reasonCode }))}>Betrieb sperren</button>
        </div>
      ))}
      <div className="partners-correction">
        <span className="partners-overline">Stammdaten nachfordern</span>
        {detail.data_correction_request ? (
          <div className="partners-lock-state is-admin">
            <AlertTriangle size={13} />
            <strong>Offene Nachforderung</strong>
            <span>{detail.data_correction_request} · {datumUndZeit(detail.data_correction_requested_at, { jahr: 'voll', leer: '–' })}</span>
            {canManage && <button className="is-unlock" disabled={busy} onClick={() => submit(() => onClearCorrection(detail.id))}>Anfrage schließen</button>}
          </div>
        ) : (
          <small className="partners-correction-hint">Keine offene Nachforderung. Der Partner ändert Steuernummer, Anschrift und Firmenname selbst im Portal; das Speichern schließt die Anfrage.</small>
        )}
        {canManage && (
          <div className="partners-lock-form">
            <input value={correction} onChange={(event) => setCorrection(event.target.value)} placeholder="Was soll korrigiert werden? (z. B. Steuernummer ungültig, Anschrift unvollständig)" aria-label="Nachforderung Stammdaten" />
            <button className="is-correction" disabled={busy || !correction.trim()} onClick={() => submit(async () => { await onRequestCorrection(detail.id, correction.trim()); setCorrection(''); })}>Korrektur anfordern</button>
          </div>
        )}
      </div>
      {(locks.events || []).length > 0 && (
        <div className="partners-lock-events">
          <span className="partners-overline">Protokoll · unveränderlich</span>
          {locks.events.slice().reverse().map((ev) => (
            <div key={ev.id}>
              <strong>{ev.action === 'lock' ? 'Gesperrt' : 'Entsperrt'}</strong>
              <span>{ev.reason || '–'}</span>
              <small>{ev.actor_name || 'Admin'} · {datumUndZeit(ev.created_at, { jahr: 'voll', leer: '–' })}</small>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

const eurFmt = value => `${Number(value || 0).toLocaleString('de-DE', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
})} €`;

const fmtDate = (value, withTime = false) => (withTime ? datumUndZeit(value) : nurDatum(value));

const initials = name => String(name || '?')
  .split(/\s+/)
  .filter(Boolean)
  .slice(0, 2)
  .map(part => part.charAt(0))
  .join('')
  .toUpperCase();

const ChecklistChip = ({ ok, label }) => (
  <span className={`partners-check-chip ${ok ? 'is-complete' : ''}`}>
    {ok ? <Check size={12} /> : <Circle size={12} />} {label}
  </span>
);

// Partner hat im Portal die Löschung beantragt (= Kündigung § 8). Dispatch ist
// bereits gestoppt. Diese Queue bewahrt die bestehende irreversible Abwicklung
// samt offener-Beträge-Hinweis, Aktivfahrt-Gate und expliziter Bestätigung.
function DeletionQueue({ canComplete, onCompleted, refreshToken, onCount }) {
  const [requests, setRequests] = useState([]);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    api.get('/admin/deletion-requests')
      .then(response => {
        const open = (response.data.requests || []).filter(item => item.status === 'open');
        setRequests(open);
        onCount?.(open.length);
      })
      .catch(() => {
        setRequests([]);
        onCount?.(0);
      });
  }, [onCount]);

  useEffect(() => { load(); }, [load, refreshToken]);

  const complete = async request => {
    const ok = window.confirm(
      `Abwicklung für "${request.company_name}" abschließen?\n\n`
      + 'Voraussetzung: offene Beträge sind abgerechnet (letzte Auszahlung/Abrechnung erstellt).\n\n'
      + 'Danach: Flotte freigegeben, Konto deaktiviert, Zugangsdaten anonymisiert. '
      + 'Firmenname, Belege und Auftragsbuch bleiben (Aufbewahrungsfristen). NICHT rückgängig zu machen.',
    );
    if (!ok) return;
    setBusy(request.id);
    try {
      await api.post(`/admin/deletion-requests/${request.id}/complete`, { confirm: true });
      toast.success('Abwicklung abgeschlossen — Konto deaktiviert & anonymisiert');
      load();
      onCompleted?.();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Abwicklung fehlgeschlagen');
    } finally {
      setBusy(null);
    }
  };

  if (requests.length === 0) return null;

  return (
    <section className="partners-deletion-queue">
      <header>
        <div className="partners-deletion-title">
          <span><AlertTriangle size={16} /></span>
          <div>
            <small>Kündigungen & Abwicklung</small>
            <h2>{requests.length} offene {requests.length === 1 ? 'Löschanfrage' : 'Löschanfragen'}</h2>
          </div>
        </div>
        <p>Dispatch ist bereits gestoppt. Abschluss erst nach Abrechnung und ohne laufende Fahrt.</p>
      </header>

      <div className="partners-deletion-list">
        {requests.map(request => (
          <article key={request.id}>
            <div className="partners-deletion-company">
              <span>{initials(request.company_name)}</span>
              <div>
                <strong>{request.company_name || request.subject_id}</strong>
                <small>Beantragt {fmtDate(request.requested_at, true)}</small>
                {request.reason && <p>Grund: „{request.reason}“</p>}
              </div>
            </div>
            <div className="partners-deletion-facts">
              <span><UsersRound size={12} /> {request.driver_count || 0} Fahrer</span>
              <span className={request.open_rides > 0 ? 'is-blocked' : ''}>
                <Truck size={12} /> {request.open_rides || 0} laufende Fahrten
              </span>
              <span>Offene Kontobewegung bei VOOGO {eurFmt(request.pending_ledger_effect)}</span>
              <span>Quelle: nachträglich unveränderbare Buchungszeilen</span>
            </div>
            {canComplete && (
              <button
                className="partners-deletion-complete"
                onClick={() => complete(request)}
                disabled={busy === request.id || request.open_rides > 0}
                title={request.open_rides > 0
                  ? 'Erst laufende Fahrten abschließen oder neu zuweisen'
                  : 'Voraussetzung: Beträge abgerechnet'}
              >
                {busy === request.id ? 'Wird abgeschlossen…' : 'Abwicklung abschließen'}
              </button>
            )}
          </article>
        ))}
      </div>
    </section>
  );
}

export default function Partners() {
  const { can } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [partners, setPartners] = useState([]);
  // ⌘K-Deep-Links (/partners?detail=<id> bzw. ?browse=1): mit Parameter startet die Liste
  // auf „Alle" — beim Detail, damit die Zeile unabhängig vom Freigabestatus sichtbar sein
  // kann; beim Stöbern als Gesamtbestand. Ohne Parameter unverändert „In Prüfung".
  const [filter, setFilter] = useState(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      return (params.get('detail') || params.get('browse')) ? 'all' : 'pending';
    } catch { return 'pending'; }
  });
  // Seitengröße 25/50/100 (Owner-Entscheid 26.08.; Server clampt auf max. 100, Default 25).
  const [pageSize, setPageSize] = useState(PAGE_SIZE);
  const [searchDraft, setSearchDraft] = useState('');
  const [appliedSearch, setAppliedSearch] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [loading, setLoading] = useState(true);
  const [listError, setListError] = useState(false);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [expanded, setExpanded] = useState(null);
  const [reviewDocId, setReviewDocId] = useState(null);   // Prüfbereich an Ort und Stelle
  const [partnerDetail, setPartnerDetail] = useState(null);
  const [partnerFile, setPartnerFile] = useState(null);   // ADMIN-UX-HEUTE: Akten-Anhang
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectTarget, setRejectTarget] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [newPartner, setNewPartner] = useState({
    company_name: '',
    name: '',
    email: '',
    phone: '',
    password: '',
  });
  const [stats, setStats] = useState({ total_partners: null, pending_partners: null });
  const [deletionCount, setDeletionCount] = useState(0);
  const [deletionRefresh, setDeletionRefresh] = useState(0);
  const listRequestRef = useRef(0);
  const detailRequestRef = useRef(0);
  const loadPartnersRef = useRef(null);
  const expandedRef = useRef(null);
  const offsetRef = useRef(0);
  const filterRef = useRef(null);
  const pendingDetailRef = useRef(null);   // ⌘K-Deep-Link: Detail nach Listen-Reset öffnen

  const loadStats = useCallback(() => {
    api.get('/admin/dashboard')
      .then(response => setStats({
        total_partners: response.data.stats?.total_partners ?? null,
        pending_partners: response.data.stats?.pending_partners ?? null,
      }))
      .catch(() => setStats({ total_partners: null, pending_partners: null }));
  }, []);

  useEffect(() => { loadStats(); }, [loadStats]);

  const loadPartners = useCallback(() => {
    const requestId = ++listRequestRef.current;
    const params = { limit: pageSize, offset };
    if (filter !== 'all') params.status = filter;
    if (appliedSearch) params.search = appliedSearch;
    if (dateFrom) params.from = dateFrom;
    if (dateTo) params.to = dateTo;

    setLoading(true);
    setListError(false);
    api.get('/admin/partners', { params })
      .then(response => {
        if (requestId !== listRequestRef.current) return;
        const nextPartners = Array.isArray(response.data.partners) ? response.data.partners : [];
        const nextTotal = Number.isSafeInteger(response.data.total) ? response.data.total : 0;
        if ((nextTotal === 0 && offset > 0) || (nextTotal > 0 && offset >= nextTotal)) {
          setOffset(nextTotal === 0 ? 0 : Math.floor((nextTotal - 1) / pageSize) * pageSize);
          return;
        }
        setPartners(nextPartners);
        setTotal(nextTotal);
      })
      .catch(() => {
        if (requestId !== listRequestRef.current) return;
        // Der Fehlerzustand verdeckt die letzte Seite. Deren bestätigtes total
        // bleibt erhalten, damit Seite 2 nicht fälschlich zu „2 von 1" wird
        // und Retry exakt denselben Offset erneut anfragt.
        setListError(true);
        toast.error('Partner konnten nicht geladen werden');
      })
      .finally(() => {
        if (requestId === listRequestRef.current) setLoading(false);
      });
  }, [filter, appliedSearch, dateFrom, dateTo, offset, pageSize]);

  const invalidateDetail = useCallback(() => {
    detailRequestRef.current += 1;
    setExpanded(null);
    setPartnerDetail(null);
    setPartnerFile(null);
    setDetailLoading(false);
    setDetailError(false);
  }, []);

  const loadPartnerDetail = useCallback((id) => {
    const requestId = ++detailRequestRef.current;
    setPartnerDetail(null);
    setPartnerFile(null);
    setDetailError(false);
    setDetailLoading(true);
    // ADMIN-UX-HEUTE-AKTEN-EINHEIT-001: Akten-Anhang parallel laden — scheitert nur der
    // Anhang, bleibt das bewährte Detail vollständig nutzbar (Anhang-Abschnitte fehlen dann).
    api.get(`/admin/partners/${id}/file`)
      .then(response => {
        if (requestId !== detailRequestRef.current) return;
        setPartnerFile(response.data || null);
      })
      .catch(() => {});
    api.get('/admin/partners', { params: { detail_id: id } })
      .then(response => {
        if (requestId !== detailRequestRef.current) return;
        if (!response.data.partner) {
          setDetailError(true);
          return;
        }
        setPartnerDetail(response.data.partner);
      })
      .catch(() => {
        if (requestId !== detailRequestRef.current) return;
        setDetailError(true);
      })
      .finally(() => {
        if (requestId === detailRequestRef.current) setDetailLoading(false);
      });
  }, []);
  loadPartnersRef.current = loadPartners;
  expandedRef.current = expanded;
  offsetRef.current = offset;
  filterRef.current = filter;

  const toggleDetail = id => {
    if (expanded === id) {
      invalidateDetail();
      return;
    }
    setExpanded(id);
    loadPartnerDetail(id);
  };

  useEffect(() => {
    invalidateDetail();
    setRejectTarget(null);
    setRejectReason('');
    loadPartners();
    // ⌘K-Deep-Link: ein vorgemerktes Detail nach dem Reset direkt wieder öffnen
    // (ohne Vormerkung — der Normalfall — ändert dieser Block nichts).
    const pending = pendingDetailRef.current;
    if (pending) {
      pendingDetailRef.current = null;
      setExpanded(pending);
      loadPartnerDetail(pending);
    }
  }, [invalidateDetail, loadPartners, loadPartnerDetail]);

  // ⌘K-Palette: /partners?detail=<id> öffnet die vorhandene Detail-Zeile — auch bei erneuter
  // Navigation auf derselben Seite. Steht die Liste nicht auf „Alle", wird erst umgeschaltet
  // und das Detail über die Vormerkung nach dem Reset geöffnet. /partners?browse=1 springt in
  // den Arbeitsbereich „Alle" (Stöbern). Ohne Parameter passiert nichts.
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const raw = params.get('detail');
    const id = Number(raw);
    if (raw && Number.isInteger(id) && id > 0) {
      if (filterRef.current !== 'all' || offsetRef.current !== 0) {
        pendingDetailRef.current = id;
        setFilter('all');
        setOffset(0);
        return;
      }
      setExpanded(id);
      loadPartnerDetail(id);
      return;
    }
    if (params.get('browse') && (filterRef.current !== 'all' || offsetRef.current !== 0)) {
      setFilter('all');
      setOffset(0);
    }
  }, [location, loadPartnerDetail]);

  const applySearch = () => {
    const next = searchDraft.trim();
    if (next === appliedSearch) {
      if (offset === 0) loadPartners();
      else setOffset(0);
      return;
    }
    setOffset(0);
    setAppliedSearch(next);
  };

  const clearFilters = () => {
    const alreadyClear = !searchDraft && !appliedSearch && !dateFrom && !dateTo;
    setSearchDraft('');
    setAppliedSearch('');
    setDateFrom('');
    setDateTo('');
    if (offset !== 0) setOffset(0);
    else if (alreadyClear) loadPartners();
  };

  const refreshAll = () => {
    loadStats();
    loadPartners();
    setDeletionRefresh(value => value + 1);
  };

  const create = async () => {
    if (!newPartner.company_name || !newPartner.name || !newPartner.email || !newPartner.password) {
      toast.error('Firma, Name, E-Mail und Passwort sind nötig');
      return;
    }
    try {
      await api.post('/admin/partners', newPartner);
      toast.success('Partner angelegt');
      setShowNew(false);
      setNewPartner({ company_name: '', name: '', email: '', phone: '', password: '' });
      loadStats();
      if (offsetRef.current === 0) loadPartnersRef.current?.();
      else setOffset(0);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler');
    }
  };

  const remove = async partner => {
    if (!window.confirm(`Partner „${partner.company_name}" und Zugang wirklich löschen? Zugeordnete Fahrzeuge werden entfernt.`)) return;
    try {
      await api.delete(`/admin/partners/${partner.id}`);
      toast.success('Partner gelöscht');
      if (expandedRef.current === partner.id) invalidateDetail();
      loadStats();
      loadPartnersRef.current?.();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler');
    }
  };

  // Partner-Compliance-Entscheidung ausschließlich über die dedizierten
  // Lifecycle-Routen; das bestehende Dokument-Gate bleibt unverändert.
  const approve = async id => {
    try {
      await api.patch(`/admin/partners/${id}/approve`);
      toast.success('Partner freigegeben!');
      if (expandedRef.current === id) invalidateDetail();
      loadStats();
      loadPartnersRef.current?.();
    } catch (error) {
      toast.error(decisionErrorMessage(error.response?.data), {
        duration: 9000,
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  const setBankStatus = async (id, status) => {
    try {
      await api.patch(`/admin/partners/${id}/bank`, { status });
      toast.success(status === 'approved'
        ? 'Bankverbindung freigegeben'
        : 'Bankverbindung abgelehnt');
      loadPartnersRef.current?.();
      if (expandedRef.current === id) loadPartnerDetail(id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Fehler');
    }
  };

  // ADMIN-FAHRZEUG-PARTNER-SPERRE-001: Betriebssperre / Entsperren / Stammdaten-Nachforderung (freigaben.manage).
  const afterPartnerMutation = async (id) => {
    loadPartnersRef.current?.();
    if (expandedRef.current === id) loadPartnerDetail(id);
  };
  const lockPartner = async (id, { reason, reason_code }) => {
    try {
      const response = await api.post(`/admin/partners/${id}/account-lock`, { reason, reason_code });
      toast.success(response.data?.message || 'Betrieb gesperrt');
      await afterPartnerMutation(id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Sperren fehlgeschlagen');
    }
  };
  const unlockPartner = async (id, reason) => {
    try {
      const response = await api.delete(`/admin/partners/${id}/account-lock`, { data: { reason: reason || undefined } });
      toast.success(response.data?.message || 'Sperre aufgehoben');
      await afterPartnerMutation(id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Entsperren fehlgeschlagen');
    }
  };
  const requestCorrection = async (id, reason) => {
    try {
      const response = await api.post(`/admin/partners/${id}/data-correction`, { reason });
      toast.success(response.data?.message || 'Korrektur angefordert');
      await afterPartnerMutation(id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Nachforderung fehlgeschlagen');
    }
  };
  const clearCorrection = async (id) => {
    try {
      await api.delete(`/admin/partners/${id}/data-correction`);
      toast.success('Nachforderung geschlossen');
      await afterPartnerMutation(id);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Schließen fehlgeschlagen');
    }
  };

  const reject = async id => {
    if (!rejectReason.trim()) {
      toast.error('Bitte Ablehnungsgrund angeben');
      return;
    }
    try {
      await api.patch(`/admin/partners/${id}/reject`, { reason: rejectReason });
      toast.success('Partner abgelehnt');
      setRejectTarget(null);
      setRejectReason('');
      if (expandedRef.current === id) invalidateDetail();
      loadStats();
      loadPartnersRef.current?.();
    } catch (error) {
      toast.error(decisionErrorMessage(error.response?.data), {
        duration: 9000,
        style: { whiteSpace: 'pre-line' },
      });
    }
  };

  const currentFilter = FILTERS.find(item => item.value === filter) || FILTERS[0];
  const pageStart = total === 0 ? 0 : offset + 1;
  const pageEnd = total === 0 ? 0 : Math.min(offset + partners.length, total);
  const pageNumber = Math.floor(offset / pageSize) + 1;
  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const hasPreviousPage = offset > 0;
  const hasNextPage = offset + partners.length < total;

  return (
    <div className="partners-page">
      {reviewDocId && <ReviewDrawerById documentId={reviewDocId} onClose={() => setReviewDocId(null)} onDecided={() => { loadPartners(); if (expanded) loadPartnerDetail(expanded); }} />}
      <PageHeader
        title="Partnerverwaltung"
        description="Flottenpartner prüfen, freigeben und sicher durch ihren Kontolebenszyklus führen."
        actions={
        <div className="partners-heading-actions">
          <span className="partners-total-badge">
            <Building2 size={12} />
            {stats.total_partners == null ? '—' : Number(stats.total_partners).toLocaleString('de-DE')} Gesamtpartner
          </span>
          <span className={Number(stats.pending_partners || 0) > 0 ? 'partners-pending-badge' : ''}>
            <FileCheck2 size={12} />
            {stats.pending_partners == null ? '—' : Number(stats.pending_partners).toLocaleString('de-DE')} ausstehend
          </span>
          {deletionCount > 0 && (
            <span className="partners-deletion-badge"><Trash2 size={12} /> {deletionCount} Kündigung</span>
          )}
          <button onClick={refreshAll}><RefreshCw size={13} /> Aktualisieren</button>
        </div>
      }
      />

      <section className="partners-workspaces" aria-label="Partner-Arbeitsbereiche">
        {FILTERS.map(item => (
          <button
            key={item.value}
            className={filter === item.value ? 'is-active' : ''}
            onClick={() => {
              setFilter(item.value);
              setOffset(0);
            }}
          >
            {item.value === 'pending' && <ShieldCheck size={16} />}
            {item.value === 'approved' && <Check size={16} />}
            {item.value === 'rejected' && <X size={16} />}
            {item.value === 'all' && <Search size={16} />}
            {item.value === 'deleted' && <Trash2 size={16} />}
            <span><strong>{item.label}</strong><small>{item.hint}</small></span>
          </button>
        ))}
      </section>

      <DeletionQueue
        canComplete={can('partners.delete')}
        onCompleted={() => {
          invalidateDetail();
          loadStats();
          loadPartnersRef.current?.();
        }}
        refreshToken={deletionRefresh}
        onCount={setDeletionCount}
      />

      <section className="partners-control-panel">
        <div className="partners-control-copy">
          <span>{filter === 'pending' ? <ShieldCheck size={15} /> : <Search size={15} />}</span>
          <div>
            <small>{filter === 'all' ? 'Seitenweiser Bestand' : filter === 'deleted' ? 'Archiv' : 'Statusqueue'}</small>
            <strong>{currentFilter.label}</strong>
          </div>
        </div>

        <label className="partners-search-field">
          <span>Firma</span>
          <div>
            <Search size={14} />
            <input
              value={searchDraft}
              onChange={event => setSearchDraft(event.target.value)}
              onKeyDown={event => event.key === 'Enter' && applySearch()}
              placeholder="Firmenname suchen …"
            />
          </div>
        </label>

        <label className="partners-date-field">
          <span>Registriert von</span>
          <input
            aria-label="Registriert von"
            type="date"
            value={dateFrom}
            onChange={event => {
              setDateFrom(event.target.value);
              setOffset(0);
            }}
          />
        </label>

        <label className="partners-date-field">
          <span>Registriert bis</span>
          <input
            aria-label="Registriert bis"
            type="date"
            value={dateTo}
            onChange={event => {
              setDateTo(event.target.value);
              setOffset(0);
            }}
          />
        </label>

        <div className="partners-search-actions">
          <button className="is-search" onClick={applySearch}><Search size={13} /> Suchen</button>
          <button className="is-clear" onClick={clearFilters}>Zurücksetzen</button>
        </div>

        <div className="partners-result-stat">
          <small>Treffer gesamt</small>
          <strong>{total}</strong>
          <span>serverseitig gefiltert</span>
        </div>
        <div className="partners-result-stat">
          <small>Seitenausschnitt</small>
          <strong>{pageStart}–{pageEnd}</strong>
          <span>max. {pageSize} pro Seite</span>
        </div>
      </section>

      <section className="partners-create-zone">
        <button className="partners-create-toggle" onClick={() => setShowNew(value => !value)}>
          {showNew ? <X size={14} /> : <Plus size={14} />}
          {showNew ? 'Anlegen abbrechen' : 'Partner anlegen'}
        </button>
        <span>Manuell angelegte Partner werden über den bestehenden Admin-Vertrag erstellt.</span>
      </section>

      {showNew && (
        <section className="partners-create-panel">
          <header>
            <div>
              <span className="partners-overline">Mietwagenunternehmer</span>
              <h2>Neuen Partner anlegen</h2>
            </div>
            <span>Firma, Ansprechpartner, E-Mail und Passwort sind Pflicht.</span>
          </header>
          <div className="partners-create-grid">
            {[
              ['company_name', 'Firma'],
              ['name', 'Ansprechpartner'],
              ['email', 'E-Mail'],
              ['phone', 'Telefon'],
              ['password', 'Passwort'],
            ].map(([key, label]) => (
              <input
                key={key}
                type={key === 'password' ? 'password' : 'text'}
                aria-label={label}
                placeholder={label}
                value={newPartner[key]}
                onChange={event => setNewPartner({ ...newPartner, [key]: event.target.value })}
              />
            ))}
            <button className="partners-create-submit" onClick={create}><Plus size={13} /> Partner anlegen</button>
          </div>
        </section>
      )}

      <section className="partners-directory-card">
        <header>
          <div>
            <span className="partners-overline">Partnerbestand · {currentFilter.label}</span>
            <h2>{filter === 'pending' ? 'Offene Partnerprüfungen' : currentFilter.label}</h2>
          </div>
          <span><FileCheck2 size={12} /> Compliance und Flottenkontext</span>
        </header>

        <div className="partners-table-scroll">
          <table className="partners-directory-table">
            <thead>
              <tr>
                <th>Firma</th>
                <th>Rolle</th>
                <th>Fahrten</th>
                <th>Status</th>
                <th>Registriert</th>
                <th>Aktionen</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <SkeletonTableRows rows={4} cols={6} />
              ) : listError ? (
                <tr>
                  <td colSpan={6}>
                    <div className="partners-detail-state partners-list-state is-error" role="alert">
                      <AlertTriangle size={16} />
                      <span>Partnerliste konnte nicht geladen werden.</span>
                      <button onClick={loadPartners}><RefreshCw size={12} /> Erneut versuchen</button>
                    </div>
                  </td>
                </tr>
              ) : partners.length === 0 ? (
                <tr>
                  <td colSpan={6}>
                    <Empty
                      icon={Building2}
                      title="Keine Partner im gewählten Ausschnitt"
                      hint="Status, Zeitraum oder optionalen Suchbegriff anpassen."
                    />
                  </td>
                </tr>
              ) : partners.map(partner => {
                const status = STATUS[partner.approval_status] || STATUS.pending;
                const isExpanded = expanded === partner.id;
                const detail = partnerDetail?.id === partner.id ? partnerDetail : null;
                const detailStatus = STATUS[detail?.approval_status] || status;
                const lifecycleMutable = partner.approval_status !== 'deleted';
                return (
                  <React.Fragment key={partner.id}>
                    <tr className={isExpanded ? 'is-expanded' : ''}>
                      <td>
                        <div className="partners-company-cell">
                          <span>{initials(partner.company_name)}</span>
                          <div>
                            <strong>{partner.company_name || 'Unbenannter Partner'}</strong>
                            <small>Partnerkonto</small>
                          </div>
                        </div>
                      </td>
                      <td><span className="partners-role">{partner.role === 'partner' ? 'Partner' : partner.role || '—'}</span></td>
                      <td className="partners-rides-cell">{Number(partner.total_rides || 0).toLocaleString('de-DE')}</td>
                      <td>
                        <span
                          className="partners-status"
                          style={{ '--partner-status': status.color }}
                        >
                          <i /> {status.label}
                        </span>
                        {partner.suspension && (
                          <small className="partners-status-flag is-locked"><LockKeyhole size={10} /> {partner.suspension.label}</small>
                        )}
                        {partner.data_correction_open && (
                          <small className="partners-status-flag is-correction"><AlertTriangle size={10} /> Nachforderung offen</small>
                        )}
                      </td>
                      <td className="partners-date-cell">{fmtDate(partner.created_at)}</td>
                      <td>
                        <div className="partners-row-actions">
                          {lifecycleMutable && partner.approval_status !== 'approved' && (
                            <button className="is-approve" onClick={() => approve(partner.id)}>Freigeben</button>
                          )}
                          {lifecycleMutable && partner.approval_status !== 'rejected' && (
                            <button className="is-reject" onClick={() => setRejectTarget(partner.id)}>Ablehnen</button>
                          )}
                          <button
                            className="is-expand"
                            aria-label={`${partner.company_name} Details ${isExpanded ? 'schließen' : 'öffnen'}`}
                            onClick={() => toggleDetail(partner.id)}
                          >
                            {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                          </button>
                          {lifecycleMutable && can('partners.delete') && (
                            <button
                              className="is-delete"
                              aria-label={`${partner.company_name} löschen`}
                              title="Partner löschen"
                              onClick={() => remove(partner)}
                            >
                              <Trash2 size={13} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>

                    {rejectTarget === partner.id && (
                      <tr className="partners-reject-row">
                        <td colSpan={6}>
                          <div>
                            <input
                              value={rejectReason}
                              onChange={event => setRejectReason(event.target.value)}
                              placeholder="Ablehnungsgrund eingeben …"
                            />
                            <button className="partners-reject-submit" onClick={() => reject(partner.id)}>Ablehnen</button>
                            <button onClick={() => {
                              setRejectTarget(null);
                              setRejectReason('');
                            }}
                            >
                              Abbrechen
                            </button>
                          </div>
                        </td>
                      </tr>
                    )}

                    {isExpanded && (
                      <tr className="partners-detail-row">
                        <td colSpan={6}>
                          {detailLoading ? (
                            <div className="partners-detail-state is-loading" role="status">
                              <RefreshCw size={16} /> Partnerdetails werden geladen …
                            </div>
                          ) : detailError ? (
                            <div className="partners-detail-state is-error" role="alert">
                              <AlertTriangle size={16} />
                              <span>Partnerdetails konnten nicht geladen werden.</span>
                              <button onClick={() => loadPartnerDetail(partner.id)}>Erneut versuchen</button>
                            </div>
                          ) : detail && (
                            <>
                              <section className="partners-detail-overview">
                            <article>
                              <UserRound size={14} />
                              <div>
                                <small>Ansprechpartner</small>
                                <strong>{detail.user_name || '—'}</strong>
                                <span>{detail.user_email || '—'}</span>
                                <span>St-Nr: {detail.tax_id || '—'}</span>
                              </div>
                            </article>
                            <article>
                              <UsersRound size={14} />
                              <div><small>Fahrer</small><strong>{detail.drivers_count || 0}</strong><span>zugeordnet</span></div>
                            </article>
                            <article>
                              <Truck size={14} />
                              <div><small>Fahrzeuge</small><strong>{detail.vehicles_count || 0}</strong><span>im Partnerbestand</span></div>
                            </article>
                            <article>
                              <ShieldCheck size={14} />
                              <div><small>Kontostatus</small><strong>{detailStatus.label}</strong><span>Lifecycle-Vertrag</span></div>
                            </article>
                          </section>

                          <PartnerLockSection
                            detail={detail}
                            canManage={can('freigaben.manage')}
                            onLock={lockPartner}
                            onUnlock={unlockPartner}
                            onRequestCorrection={requestCorrection}
                            onClearCorrection={clearCorrection}
                          />

                          <section className="partners-detail-section">
                            <header>
                              <div><span className="partners-overline">Freigabe-Gate</span><h3>Compliance-Checkliste</h3></div>
                              <span>Entscheidung bleibt dokumentengebunden</span>
                            </header>
                            <div className="partners-checklist">
                              <ChecklistChip ok={detail.onboarding?.agbAccepted} label="AGB akzeptiert" />
                              {(detail.onboarding?.documents || []).map(item => (
                                <ChecklistChip
                                  key={item.type}
                                  ok={item.done}
                                  label={`${item.label}${item.status === 'rejected'
                                    ? ' (abgelehnt)'
                                    : item.status === 'pending' ? ' (Prüfung)' : ''}`}
                                />
                              ))}
                            </div>
                          </section>

                          <section className="partners-detail-section">
                            <header>
                              <div><span className="partners-overline">Private Assets</span><h3>Dokumente</h3></div>
                              <span>Öffnen ausschließlich im Prüfbereich</span>
                            </header>
                            {(detail.documents || []).length === 0 ? (
                              <span className="partners-no-docs">Keine Dokumente hochgeladen</span>
                            ) : (
                              <div className="partners-documents">
                                {(detail.documents || []).map(document => document.has_private_asset ? (
                                  <button
                                    key={document.id}
                                    onClick={() => setReviewDocId(document.id)}
                                  >
                                    <i className={`is-${document.status}`} />
                                    {document.display_name || DOC_LABELS[document.document_type] || document.document_type}
                                    <ExternalLink size={11} />
                                  </button>
                                ) : (
                                  <span key={document.id}>
                                    <i className={`is-${document.status}`} />
                                    {document.display_name || DOC_LABELS[document.document_type] || document.document_type}
                                    {' · kein Inhalt'}
                                  </span>
                                ))}
                              </div>
                            )}
                          </section>

                          <section className="partners-detail-section">
                            <header>
                              <div><span className="partners-overline">Auszahlungsziel</span><h3>Bankverbindung</h3></div>
                              <span>Freigabe bleibt separate Adminentscheidung</span>
                            </header>
                            {detail.iban ? (
                              <div className="partners-bank-card">
                                <span className="partners-bank-icon"><Landmark size={16} /></span>
                                <div>
                                  <small>IBAN</small>
                                  <strong>{detail.iban}</strong>
                                  <span>{detail.bank_holder || 'Kein Kontoinhaber'}{detail.bic ? ` · BIC ${detail.bic}` : ''}</span>
                                </div>
                                <span
                                  className="partners-bank-status"
                                  style={{ '--partner-bank-status': STATUS[detail.bank_status]?.color || '#7a6680' }}
                                >
                                  <i />
                                  {detail.bank_status === 'approved'
                                    ? 'Freigegeben'
                                    : detail.bank_status === 'rejected' ? 'Abgelehnt' : 'Wird geprüft'}
                                </span>
                                {detail.approval_status !== 'deleted' && (
                                  <div className="partners-bank-actions">
                                    {detail.bank_status !== 'approved' && (
                                      <button className="is-approve" onClick={() => setBankStatus(partner.id, 'approved')}>Bank freigeben</button>
                                    )}
                                    {detail.bank_status !== 'rejected' && (
                                      <button className="is-reject" onClick={() => setBankStatus(partner.id, 'rejected')}>Ablehnen</button>
                                    )}
                                  </div>
                                )}
                              </div>
                            ) : (
                              <span className="partners-no-docs">Keine Bankverbindung hinterlegt</span>
                            )}
                          </section>

                              {/* ── ADMIN-UX-HEUTE-AKTEN-EINHEIT-001: Partner-AKTE — Flotte,
                                  Abrechnung und Support-Historie an derselben Stelle wie
                                  Stammdaten und Compliance (Stripe-Muster: nie woanders suchen). ── */}
                              {partnerFile && (
                                <section className="partners-detail-section">
                                  <header>
                                    <div><span className="partners-overline">Flotte</span><h3>Fahrer & Fahrzeuge</h3></div>
                                    <span>{partnerFile.drivers.length} Fahrer · {partnerFile.vehicles.length} Fahrzeuge</span>
                                  </header>
                                  <div className="partners-akte-fleet">
                                    <div>
                                      {partnerFile.drivers.length === 0 ? (
                                        <span className="partners-no-docs">Keine Fahrer zugeordnet</span>
                                      ) : partnerFile.drivers.map(driver => (
                                        <article key={driver.id}>
                                          <UsersRound size={13} />
                                          <strong>{driver.name || 'Unbenannter Fahrer'}</strong>
                                          <span className={driver.is_online ? 'is-online' : ''}>{driver.is_online ? 'Online' : 'Offline'}</span>
                                          <em>{STATUS[driver.approval_status]?.label || driver.approval_status}</em>
                                        </article>
                                      ))}
                                    </div>
                                    <div>
                                      {partnerFile.vehicles.length === 0 ? (
                                        <span className="partners-no-docs">Keine Fahrzeuge im Bestand</span>
                                      ) : partnerFile.vehicles.map(vehicle => (
                                        <article key={vehicle.id}>
                                          <Truck size={13} />
                                          <strong>{vehicle.brand} {vehicle.model}</strong>
                                          <span>{vehicle.license_plate}</span>
                                          <em>{STATUS[vehicle.approval_status]?.label || vehicle.approval_status}</em>
                                        </article>
                                      ))}
                                    </div>
                                  </div>
                                </section>
                              )}

                              {/* payouts === null heißt: kein finance.manage — Abschnitt erscheint dann gar nicht. */}
                              {partnerFile?.payouts && (
                                <section className="partners-detail-section">
                                  <header>
                                    <div><span className="partners-overline">Abrechnung</span><h3>Abrechnungsperioden</h3></div>
                                    {/* Der Betrag in jeder Zeile ist payout_cents. Er trägt hier denselben
                                        Namen wie im verbindlichen Beleg und im Partner-Portal — ohne den Namen
                                        liest sich die Zahl neben einem Zeitraum wie ein Umsatz. */}
                                    <span>neueste zuerst · Betrag = Auszahlungsbetrag · Entscheidung bleibt in der Abrechnung</span>
                                  </header>
                                  {partnerFile.payouts.length === 0 ? (
                                    <span className="partners-no-docs">Noch keine Abrechnungsperioden</span>
                                  ) : (
                                    <div className="partners-akte-payouts">
                                      {partnerFile.payouts.map(payout => (
                                        <article key={payout.id}>
                                          <strong>{fmtDate(payout.period_start)} – {fmtDate(payout.period_end)}</strong>
                                          <span className={`is-${payout.state}`}>{PAYOUT_STATE_LABELS[payout.state] || payout.state}</span>
                                          <em>{euro(payout.payout_cents)} €</em>
                                        </article>
                                      ))}
                                    </div>
                                  )}
                                </section>
                              )}

                              <PartnerCorrectionCard partnerId={partner.id} approvalStatus={detail.approval_status} />

                              {partnerFile && (
                                <section className="partners-detail-section">
                                  <header>
                                    <div><span className="partners-overline">Support</span><h3>Support-Historie dieses Kontos</h3></div>
                                    <span>Tickets und Chats des Partnerkontos</span>
                                  </header>
                                  {partnerFile.support.length === 0 ? (
                                    <span className="partners-no-docs">Keine Support-Vorgänge</span>
                                  ) : (
                                    <div className="partners-akte-support">
                                      {partnerFile.support.map(entry => (
                                        <article key={`${entry.kind}-${entry.id}`}>
                                          <strong>{entry.title}</strong>
                                          <span>{entry.kind === 'ticket' ? 'Ticket' : 'Chat'} · {SUPPORT_STATUS_LABELS[entry.status] || entry.status}</span>
                                          <small>{fmtDate(entry.at, true)}</small>
                                        </article>
                                      ))}
                                    </div>
                                  )}
                                </section>
                              )}

                              {detail.rejection_reason && (
                                <section className="partners-rejection-reason">
                                  <AlertTriangle size={14} />
                                  <div><small>Letzter Ablehnungsgrund</small><strong>{detail.rejection_reason}</strong></div>
                                </section>
                              )}
                            </>
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>

        <footer className="partners-pagination" aria-label="Partnerseiten">
          <span>{pageStart}–{pageEnd} von {total} Partnern</span>
          <div>
            {/* Seitengröße 25/50/100 — Wechsel springt auf Seite 1 (Owner-Entscheid 26.08.). */}
            <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#4b5563', marginRight: 8 }}>
              <select
                aria-label="Einträge pro Seite"
                value={pageSize}
                onChange={(event) => { setPageSize(Number(event.target.value)); setOffset(0); }}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid #e5e7eb', background: '#fff', fontSize: 12, fontWeight: 600, color: '#374151' }}
              >
                {[25, 50, 100].map((size) => <option key={size} value={size}>{size}</option>)}
              </select>
              pro Seite
            </label>
            <button
              aria-label="Vorherige Partnerseite"
              disabled={!hasPreviousPage || loading}
              onClick={() => setOffset(value => Math.max(0, value - pageSize))}
            >
              <ChevronLeft size={13} /> Zurück
            </button>
            <strong>Seite {pageNumber} von {pageCount}</strong>
            <button
              aria-label="Nächste Partnerseite"
              disabled={!hasNextPage || loading}
              onClick={() => setOffset(value => value + pageSize)}
            >
              Weiter <ChevronRight size={13} />
            </button>
          </div>
        </footer>
      </section>
    </div>
  );
}
